import { ReactElement } from "react"

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCloudDownload, faCopy } from "@fortawesome/free-solid-svg-icons"

import { InlineLoading, Loading, Modal, Tab, Tabs } from "@wfp/ui"

import { MappingSchemaData } from "interfaces/dataInterfaces"

import { useDownloadMappingExcelFileMutation } from "services/marketprices/mappingSchemas"
import { useDownloadXlsFormSchemasByIdMutation } from "services/marketprices/xlsFormSchemas"
import { useGetFetchOperationsQuery } from "services/marketprices/dashboard"
import { formatDate } from "utils/helpers"
import useCommon from "../../hooks/useCommon"

type Props = {
  data: MappingSchemaData
  title?: string
  isOpen: boolean
  onClose: () => void
  isLoading: boolean
}

const InfoRow = ({
  label,
  value,
  className,
}: {
  label: string
  value: string | number | ReactElement
  className?: string
}): ReactElement => (
  <>
    <div className="flex gap-2 text-gray-600 font-semibold">
      <span>{label}</span>
    </div>
    <div className={`text-gray-800 ${className}`}>{value}</div>
  </>
)

const DetailsModal = ({ title, data, isOpen, onClose, isLoading }: Props): ReactElement => {
  const { showSnackBar } = useCommon()
  const { data: fetchingOperations, isFetching: loadingFetchData } = useGetFetchOperationsQuery(
    { mappingSchemaId: data?.mappingSchemaId },
    { skip: !data?.mappingSchemaId || !data?.xlsFormId },
  )
  const [downloadExcelFile, { isLoading: isDownloading }] = useDownloadMappingExcelFileMutation()
  const [downloadXlsForm, { isLoading: isDownloadingXls }] = useDownloadXlsFormSchemasByIdMutation()

  const handleDownload = async (): Promise<void> => {
    if (!data?.mappingSchemaId) return
    if (data?.xlsFormId) {
      await downloadXlsForm({ fileId: data.xlsFormId, sourceName: data.mappingSchemaName }).unwrap()
      return
    }
    await downloadExcelFile({ msId: data.mappingSchemaId.toString(), fileName: data.mappingSchemaName }).unwrap()
  }

  const handleCopyJSON = async (): Promise<void> => {
    try {
      const jsonString = JSON.stringify(JSON.parse(data.mappingSchemaJSON ?? null), null, 2)
      await navigator.clipboard.writeText(jsonString)
      showSnackBar("JSON copied to clipboard successfully")
    } catch (error) {
      showSnackBar("Failed to copy JSON to clipboard", "error")
    }
  }

  const fileType = data?.xlsFormId ? "XLS Form" : "Excel File"

  return (
    <Modal
      open={isOpen}
      modalHeading={title || "Mapping Schema Details"}
      onRequestClose={onClose}
      lazyLoad
      passiveModal
    >
      <Tabs>
        <Tab label="Details" href="" role="tab" selected tabIndex={0}>
          {isLoading ? (
            <div className="min-h-[130px] d-flex align-center justify-center">
              <Loading withOverlay={false} small />
            </div>
          ) : (
            <div className="p-1">
              <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 leading-6">
                <InfoRow label="ID" value={data.mappingSchemaId} />
                <InfoRow label="Country" value={data.countryName} />
                <InfoRow label="Name" value={data.mappingSchemaName} />
                <InfoRow label="Type" value={data.mappingSchemaType} />
                <InfoRow
                  label="Created Date"
                  value={formatDate(data.mappingSchemaCreateDate || "", "DD-MMMM-YYYY HH:mm") || "N/A"}
                />
                <InfoRow
                  label="Last Updated"
                  value={formatDate(data.mappingSchemaUpdateDate || "", "DD-MMMM-YYYY HH:mm") || "N/A"}
                />
                {!!data?.xlsFormId && !!fetchingOperations?.length && (
                  <>
                    <InfoRow
                      label="Last Fetch Date"
                      value={
                        loadingFetchData ? (
                          <InlineLoading className="h-6" />
                        ) : (
                          formatDate(fetchingOperations?.[0]?.runAt || "", "DD-MMMM-YYYY HH:mm") || "N/A"
                        )
                      }
                    />
                    <InfoRow
                      label="Last Fetch Description"
                      className="text-sm leading-6"
                      value={
                        loadingFetchData ? <InlineLoading className="h-6" /> : fetchingOperations?.[0]?.message || "N/A"
                      }
                    />
                  </>
                )}
                <InfoRow
                  label={`${fileType} Source`}
                  value={
                    isDownloading || isDownloadingXls ? (
                      <InlineLoading className="h-6" />
                    ) : (
                      <button
                        disabled={isDownloading || isDownloadingXls}
                        type="button"
                        onClick={handleDownload}
                        className="border-0 bg-transparent p-0 h-full flex items-center gap-2 text-[var(--interactive-01)] hover:text-blue-700 transition-colors cursor-pointer"
                        title={data.mappingSchemaName ?? `Download ${fileType}`}
                      >
                        <FontAwesomeIcon icon={faCloudDownload} />
                      </button>
                    )
                  }
                />
              </div>
            </div>
          )}
        </Tab>

        <Tab label="Schema" href="" role="tab" selected tabIndex={0}>
          <div className="p-1">
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-sm font-semibold text-gray-700 m-0">Schema Configuration</h4>
              <FontAwesomeIcon
                icon={faCopy}
                onClick={handleCopyJSON}
                title="Copy JSON to clipboard"
                color="var(--interactive-01)"
                className="cursor-pointer"
              />
            </div>
            <div className="bg-gray-50 rounded-md border border-gray-200 p-3">
              <pre className="max-h-96 overflow-auto text-xs font-mono text-gray-800 leading-relaxed">
                {JSON.stringify(JSON.parse(data.mappingSchemaJSON ?? null), null, 2)}
              </pre>
            </div>
          </div>
        </Tab>
      </Tabs>
    </Modal>
  )
}

export default DetailsModal
